import type { GeomKind } from "@bezpart-board/shared";
import { geomVolumeCombinedPathD, geomVolumeLayers } from "./volumeShapes";

export function trianglePathD(w: number, h: number): string {
  const x1 = w / 2;
  return `M ${x1} 0 L ${w} ${h} L 0 ${h} Z`;
}

export function diamondPathD(w: number, h: number): string {
  return `M ${w / 2} 0 L ${w} ${h / 2} L ${w / 2} ${h} L 0 ${h / 2} Z`;
}

export function starPathD(w: number, h: number, points = 5): string {
  const cx = w / 2;
  const cy = h / 2;
  const outerR = Math.min(w, h) / 2;
  const innerR = outerR * 0.4;
  const verts: [number, number][] = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const angle = -Math.PI / 2 + (i * Math.PI) / points;
    verts.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
  }
  const [fx, fy] = verts[0]!;
  return (
    `M ${fx} ${fy} ` +
    verts
      .slice(1)
      .map(([x, y]) => `L ${x} ${y}`)
      .join(" ") +
    " Z"
  );
}

function polyPath(points: [number, number][]): string {
  if (points.length === 0) return "";
  const [fx, fy] = points[0]!;
  return (
    `M ${fx} ${fy} ` +
    points
      .slice(1)
      .map(([x, y]) => `L ${x} ${y}`)
      .join(" ") +
    " Z"
  );
}

function regularPolygonPathD(w: number, h: number, sides: number, rotation = -Math.PI / 2): string {
  const cx = w / 2;
  const cy = h / 2;
  const rx = w / 2;
  const ry = h / 2;
  const pts: [number, number][] = [];
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i * 2 * Math.PI) / sides;
    pts.push([cx + rx * Math.cos(a), cy + ry * Math.sin(a)]);
  }
  return polyPath(pts);
}

function roundedRectPathD(w: number, h: number, rIn?: number): string {
  const r = Math.min(rIn ?? Math.min(w, h) * 0.22, w / 2, h / 2);
  return [
    `M ${r} 0`,
    `H ${w - r}`,
    `A ${r} ${r} 0 0 1 ${w} ${r}`,
    `V ${h - r}`,
    `A ${r} ${r} 0 0 1 ${w - r} ${h}`,
    `H ${r}`,
    `A ${r} ${r} 0 0 1 0 ${h - r}`,
    `V ${r}`,
    `A ${r} ${r} 0 0 1 ${r} 0`,
    "Z",
  ].join(" ");
}

function stadiumPathD(w: number, h: number): string {
  return roundedRectPathD(w, h, Math.min(w, h) / 2);
}

function arrowHorizontalPathD(w: number, h: number, dir: "left" | "right" | "both"): string {
  const mid = h / 2;
  const head = Math.min(w * 0.38, h * 0.85);
  const shaftY0 = h * 0.28;
  const shaftY1 = h * 0.72;
  if (dir === "right") {
    return polyPath([
      [0, shaftY0],
      [w - head, shaftY0],
      [w - head, 0],
      [w, mid],
      [w - head, h],
      [w - head, shaftY1],
      [0, shaftY1],
    ]);
  }
  if (dir === "left") {
    return polyPath([
      [w, shaftY0],
      [head, shaftY0],
      [head, 0],
      [0, mid],
      [head, h],
      [head, shaftY1],
      [w, shaftY1],
    ]);
  }
  const head2 = Math.min(w * 0.28, h * 0.85);
  return polyPath([
    [0, mid],
    [head2, 0],
    [head2, shaftY0],
    [w - head2, shaftY0],
    [w - head2, 0],
    [w, mid],
    [w - head2, h],
    [w - head2, shaftY1],
    [head2, shaftY1],
    [head2, h],
  ]);
}

function arrowVerticalPathD(w: number, h: number, dir: "up" | "down" | "both"): string {
  const mid = w / 2;
  const head = Math.min(h * 0.38, w * 0.85);
  const shaftX0 = w * 0.28;
  const shaftX1 = w * 0.72;
  if (dir === "down") {
    return polyPath([
      [shaftX0, 0],
      [shaftX1, 0],
      [shaftX1, h - head],
      [w, h - head],
      [mid, h],
      [0, h - head],
      [shaftX0, h - head],
    ]);
  }
  if (dir === "up") {
    return polyPath([
      [shaftX0, h],
      [shaftX1, h],
      [shaftX1, head],
      [w, head],
      [mid, 0],
      [0, head],
      [shaftX0, head],
    ]);
  }
  const head2 = Math.min(h * 0.28, w * 0.85);
  return polyPath([
    [mid, 0],
    [w, head2],
    [shaftX1, head2],
    [shaftX1, h - head2],
    [w, h - head2],
    [mid, h],
    [0, h - head2],
    [shaftX0, h - head2],
    [shaftX0, head2],
    [0, head2],
  ]);
}

function chevronPathD(w: number, h: number, dir: "left" | "right"): string {
  const depth = w * 0.42;
  if (dir === "right") {
    return polyPath([
      [0, 0],
      [w - depth, 0],
      [w, h / 2],
      [w - depth, h],
      [0, h],
      [depth, h / 2],
    ]);
  }
  return polyPath([
    [w, 0],
    [depth, 0],
    [0, h / 2],
    [depth, h],
    [w, h],
    [w - depth, h / 2],
  ]);
}

function cloudPathD(w: number, h: number): string {
  return [
    `M ${0.12 * w} ${0.62 * h}`,
    `C ${0.05 * w} ${0.62 * h} ${0.02 * w} ${0.5 * h} ${0.1 * w} ${0.45 * h}`,
    `C ${0.08 * w} ${0.28 * h} ${0.22 * w} ${0.2 * h} ${0.34 * w} ${0.28 * h}`,
    `C ${0.4 * w} ${0.12 * h} ${0.62 * w} ${0.1 * h} ${0.68 * w} ${0.28 * h}`,
    `C ${0.82 * w} ${0.2 * h} ${0.98 * w} ${0.32 * h} ${0.92 * w} ${0.48 * h}`,
    `C ${1.02 * w} ${0.55 * h} ${0.98 * w} ${0.72 * h} ${0.86 * w} ${0.74 * h}`,
    `C ${0.8 * w} ${0.9 * h} ${0.55 * w} ${0.92 * h} ${0.42 * w} ${0.8 * h}`,
    `C ${0.3 * w} ${0.92 * h} ${0.12 * w} ${0.85 * h} ${0.12 * w} ${0.62 * h}`,
    "Z",
  ].join(" ");
}

function bubblePathD(w: number, h: number): string {
  const r = Math.min(w, h) * 0.18;
  const bodyH = h * 0.72;
  return [
    `M ${r} 0`,
    `H ${w - r}`,
    `A ${r} ${r} 0 0 1 ${w} ${r}`,
    `V ${bodyH - r}`,
    `A ${r} ${r} 0 0 1 ${w - r} ${bodyH}`,
    `L ${w * 0.38} ${bodyH}`,
    `L ${w * 0.22} ${h}`,
    `L ${w * 0.28} ${bodyH}`,
    `H ${r}`,
    `A ${r} ${r} 0 0 1 0 ${bodyH - r}`,
    `V ${r}`,
    `A ${r} ${r} 0 0 1 ${r} 0`,
    "Z",
  ].join(" ");
}

function thoughtPathD(w: number, h: number): string {
  const main = [
    `M ${0.5 * w} ${0.08 * h}`,
    `C ${0.78 * w} ${0.08 * h} ${0.95 * w} ${0.28 * h} ${0.92 * w} ${0.48 * h}`,
    `C ${0.98 * w} ${0.62 * h} ${0.82 * w} ${0.78 * h} ${0.58 * w} ${0.76 * h}`,
    `C ${0.42 * w} ${0.88 * h} ${0.18 * w} ${0.82 * h} ${0.14 * w} ${0.62 * h}`,
    `C ${0.02 * w} ${0.52 * h} ${0.08 * w} ${0.22 * h} ${0.32 * w} ${0.18 * h}`,
    `C ${0.36 * w} ${0.1 * h} ${0.42 * w} ${0.08 * h} ${0.5 * w} ${0.08 * h}`,
    "Z",
  ].join(" ");
  const b1cx = 0.28 * w;
  const b1cy = 0.88 * h;
  const b1r = 0.07 * Math.min(w, h);
  const b2cx = 0.18 * w;
  const b2cy = 0.96 * h;
  const b2r = 0.045 * Math.min(w, h);
  return [
    main,
    `M ${b1cx - b1r} ${b1cy} A ${b1r} ${b1r} 0 1 1 ${b1cx + b1r} ${b1cy} A ${b1r} ${b1r} 0 1 1 ${b1cx - b1r} ${b1cy} Z`,
    `M ${b2cx - b2r} ${b2cy} A ${b2r} ${b2r} 0 1 1 ${b2cx + b2r} ${b2cy} A ${b2r} ${b2r} 0 1 1 ${b2cx - b2r} ${b2cy} Z`,
  ].join(" ");
}

function bracePathD(w: number, h: number): string {
  const mid = h / 2;
  const inset = w * 0.35;
  return [
    `M ${w * 0.85} 0`,
    `C ${w * 0.55} 0 ${w * 0.55} ${mid * 0.55} ${inset} ${mid * 0.7}`,
    `C ${w * 0.15} ${mid * 0.85} ${w * 0.15} ${mid} ${inset * 0.35} ${mid}`,
    `C ${w * 0.15} ${mid} ${w * 0.15} ${mid * 1.15} ${inset} ${mid * 1.3}`,
    `C ${w * 0.55} ${mid * 1.45} ${w * 0.55} ${h} ${w * 0.85} ${h}`,
    `L ${w * 0.95} ${h}`,
    `C ${w * 0.7} ${h} ${w * 0.7} ${mid * 1.55} ${w * 0.42} ${mid * 1.35}`,
    `C ${w * 0.28} ${mid * 1.2} ${w * 0.28} ${mid} ${w * 0.12} ${mid}`,
    `C ${w * 0.28} ${mid} ${w * 0.28} ${mid * 0.8} ${w * 0.42} ${mid * 0.65}`,
    `C ${w * 0.7} ${mid * 0.45} ${w * 0.7} 0 ${w * 0.95} 0`,
    "Z",
  ].join(" ");
}

function heartPathD(w: number, h: number): string {
  return [
    `M ${w / 2} ${h * 0.92}`,
    `C ${w * 0.1} ${h * 0.65} ${0} ${h * 0.38} ${0} ${h * 0.28}`,
    `C ${0} ${h * 0.1} ${w * 0.14} 0 ${w * 0.28} 0`,
    `C ${w * 0.4} 0 ${w / 2} ${h * 0.1} ${w / 2} ${h * 0.22}`,
    `C ${w / 2} ${h * 0.1} ${w * 0.6} 0 ${w * 0.72} 0`,
    `C ${w * 0.86} 0 ${w} ${h * 0.1} ${w} ${h * 0.28}`,
    `C ${w} ${h * 0.38} ${w * 0.9} ${h * 0.65} ${w / 2} ${h * 0.92}`,
    "Z",
  ].join(" ");
}

function crossPathD(w: number, h: number): string {
  const t = Math.min(w, h) * 0.28;
  const cx = w / 2;
  const cy = h / 2;
  return polyPath([
    [cx - t / 2, 0],
    [cx + t / 2, 0],
    [cx + t / 2, cy - t / 2],
    [w, cy - t / 2],
    [w, cy + t / 2],
    [cx + t / 2, cy + t / 2],
    [cx + t / 2, h],
    [cx - t / 2, h],
    [cx - t / 2, cy + t / 2],
    [0, cy + t / 2],
    [0, cy - t / 2],
    [cx - t / 2, cy - t / 2],
  ]);
}

function plusPathD(w: number, h: number): string {
  const t = Math.min(w, h) * 0.34;
  const cx = w / 2;
  const cy = h / 2;
  return polyPath([
    [cx - t / 2, 0],
    [cx + t / 2, 0],
    [cx + t / 2, cy - t / 2],
    [w, cy - t / 2],
    [w, cy + t / 2],
    [cx + t / 2, cy + t / 2],
    [cx + t / 2, h],
    [cx - t / 2, h],
    [cx - t / 2, cy + t / 2],
    [0, cy + t / 2],
    [0, cy - t / 2],
    [cx - t / 2, cy - t / 2],
  ]);
}

function crescentPathD(w: number, h: number): string {
  const cx = w * 0.48;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.48;
  const or = r * 0.78;
  return [
    `M ${cx} ${cy - r}`,
    `A ${r} ${r} 0 1 0 ${cx} ${cy + r}`,
    `A ${or} ${or} 0 1 1 ${cx} ${cy - r}`,
    "Z",
  ].join(" ");
}

function rightTrianglePathD(w: number, h: number): string {
  return polyPath([
    [0, 0],
    [w, h],
    [0, h],
  ]);
}

function leftTrianglePathD(w: number, h: number): string {
  return polyPath([
    [w, 0],
    [w, h],
    [0, h],
  ]);
}

function trapezoidPathD(w: number, h: number): string {
  const inset = w * 0.18;
  return polyPath([
    [inset, 0],
    [w - inset, 0],
    [w, h],
    [0, h],
  ]);
}

function parallelogramPathD(w: number, h: number, flip = false): string {
  const skew = w * 0.22;
  if (flip) {
    return polyPath([
      [0, 0],
      [w - skew, 0],
      [w, h],
      [skew, h],
    ]);
  }
  return polyPath([
    [skew, 0],
    [w, 0],
    [w - skew, h],
    [0, h],
  ]);
}

function rhomboidPathD(w: number, h: number): string {
  return parallelogramPathD(w, h, true);
}

function bannerPathD(w: number, h: number): string {
  const notch = h * 0.28;
  return polyPath([
    [0, 0],
    [w, 0],
    [w, h],
    [w / 2, h - notch],
    [0, h],
  ]);
}

function documentPathD(w: number, h: number): string {
  const fold = Math.min(w, h) * 0.28;
  return polyPath([
    [0, 0],
    [w - fold, 0],
    [w, fold],
    [w, h],
    [0, h],
  ]);
}

function ringPathD(w: number, h: number): string {
  const cx = w / 2;
  const cy = h / 2;
  const ro = Math.min(w, h) / 2;
  const ri = ro * 0.55;
  return [
    `M ${cx} ${cy - ro}`,
    `A ${ro} ${ro} 0 1 1 ${cx - 0.001} ${cy - ro}`,
    "Z",
    `M ${cx} ${cy - ri}`,
    `A ${ri} ${ri} 0 1 0 ${cx + 0.001} ${cy - ri}`,
    "Z",
  ].join(" ");
}

export function geomPathD(kind: GeomKind, w: number, h: number): string {
  const volume = geomVolumeCombinedPathD(kind, w, h);
  if (volume) return volume;

  switch (kind) {
    case "arrow_leftright":
      return arrowHorizontalPathD(w, h, "both");
    case "arrow_left":
      return arrowHorizontalPathD(w, h, "left");
    case "arrow_right":
      return arrowHorizontalPathD(w, h, "right");
    case "arrow_updown":
      return arrowVerticalPathD(w, h, "both");
    case "arrow_up":
      return arrowVerticalPathD(w, h, "up");
    case "arrow_down":
      return arrowVerticalPathD(w, h, "down");
    case "chevron_right":
      return chevronPathD(w, h, "right");
    case "chevron_left":
      return chevronPathD(w, h, "left");
    case "cloud":
      return cloudPathD(w, h);
    case "bubble":
      return bubblePathD(w, h);
    case "thought":
      return thoughtPathD(w, h);
    case "brace":
      return bracePathD(w, h);
    case "heart":
      return heartPathD(w, h);
    case "cross":
      return crossPathD(w, h);
    case "plus":
      return plusPathD(w, h);
    case "crescent":
      return crescentPathD(w, h);
    case "rounded_rect":
      return roundedRectPathD(w, h);
    case "stadium":
      return stadiumPathD(w, h);
    case "hexagon":
      return regularPolygonPathD(w, h, 6, 0);
    case "pentagon":
      return regularPolygonPathD(w, h, 5);
    case "octagon":
      return regularPolygonPathD(w, h, 8, Math.PI / 8);
    case "right_triangle":
      return rightTrianglePathD(w, h);
    case "left_triangle":
      return leftTrianglePathD(w, h);
    case "trapezoid":
      return trapezoidPathD(w, h);
    case "parallelogram":
      return parallelogramPathD(w, h, false);
    case "parallelogram_alt":
      return parallelogramPathD(w, h, true);
    case "rhomboid":
      return rhomboidPathD(w, h);
    case "banner":
      return bannerPathD(w, h);
    case "document":
      return documentPathD(w, h);
    case "ring":
      return ringPathD(w, h);
    case "sphere":
    case "frustum":
    case "cone":
    case "cylinder":
    case "pyramid":
    case "pyramid_tri":
    case "box":
    case "oblique_box":
      return geomVolumeLayers(kind, w, h)?.map((l) => l.d).join(" ") ?? diamondPathD(w, h);
  }
}

const MIN_BBOX = 8;

/** Drag bbox: Shift — квадрат, Alt — от центра (anchor = start). */
export function bboxFromPointerDrag(
  start: { x: number; y: number },
  end: { x: number; y: number },
  shiftKey: boolean,
  altKey: boolean
): { x: number; y: number; w: number; h: number } | null {
  let w = altKey ? Math.abs(end.x - start.x) * 2 : Math.abs(end.x - start.x);
  let h = altKey ? Math.abs(end.y - start.y) * 2 : Math.abs(end.y - start.y);

  if (shiftKey) {
    const size = Math.max(w, h);
    w = size;
    h = size;
  }

  if (w < MIN_BBOX || h < MIN_BBOX) return null;

  let x: number;
  let y: number;

  if (altKey) {
    x = start.x - w / 2;
    y = start.y - h / 2;
  } else if (shiftKey) {
    x = end.x < start.x ? start.x - w : start.x;
    y = end.y < start.y ? start.y - h : start.y;
  } else {
    x = Math.min(start.x, end.x);
    y = Math.min(start.y, end.y);
  }

  return {
    x: Math.round(x),
    y: Math.round(y),
    w: Math.round(w),
    h: Math.round(h),
  };
}
